import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SeverityBadge, ReviewBadge, Money, Variance } from "../components/badges";
import { BulkReviewBar } from "../components/BulkReviewBar";
import { useBulkReview } from "../hooks/useBulkReview";
import { num, fmtDate, SEVERITY, sevDot } from "../lib/format";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { ChevronRight, ChevronDown, AlertTriangle, Users, ListFilter, X, MessageSquare } from "lucide-react";

const fmtNum = (v) => (v == null || isNaN(v) ? "—" : num(v, 2));

// Compact dropdown multi-select used for the Error and Severity filters.
function FilterDropdown({ label, testid, options, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const active = selected.size;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 h-9 text-sm font-semibold transition-colors ${
            active ? "border-foreground bg-secondary text-foreground" : "border-border text-muted-foreground hover:text-foreground"
          }`}
        >
          <ListFilter size={14} /> {label}
          {active ? <span className="rounded-full bg-foreground px-1.5 text-[11px] font-bold text-background">{active}</span> : null}
          <ChevronDown size={14} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
          <button
            data-testid={`${testid}-clear`}
            onClick={onClear}
            disabled={!active}
            className="text-xs font-semibold text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-40"
          >
            All
          </button>
        </div>
        <div className="max-h-72 overflow-auto py-1">
          {options.map((o) => (
            <label
              key={o.value}
              data-testid={`${testid}-opt-${o.value}`}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-sm hover:bg-muted/60"
            >
              <Checkbox checked={selected.has(o.value)} onCheckedChange={() => onToggle(o.value)} />
              {o.dot ? <span className={`h-2 w-2 shrink-0 rounded-full ${o.dot}`} /> : null}
              <span className="min-w-0 flex-1 truncate">{o.label}</span>
              {o.count != null ? <span className="font-mono text-xs text-muted-foreground">{o.count}</span> : null}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Popover showing the full workflow-note history for a finding (lazy-loaded).
function NotePopover({ guid, latestNote, reviewedBy, reviewedAt }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["finding-history", guid],
    queryFn: async () => (await api.get(`/findings/${guid}/history`)).data,
    enabled: open,
  });
  const history = data?.history || [];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`note-indicator-${guid}`}
          onClick={(e) => e.stopPropagation()}
          title="View workflow note history"
          className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground hover:border-foreground/40 hover:text-foreground"
        >
          <MessageSquare size={12} /> Note
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Workflow notes
        </div>
        <div className="max-h-72 overflow-auto p-3" data-testid={`note-history-${guid}`}>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : history.length ? (
            <ul className="space-y-3">
              {history.map((h, i) => (
                <li key={`${h.reviewed_at}-${h.review_status}-${i}`} className="border-l-2 border-border pl-3">
                  <div className="flex items-center gap-2">
                    <ReviewBadge status={h.review_status} />
                    <span className="font-mono text-[11px] text-muted-foreground">{fmtDate(h.reviewed_at)}</span>
                  </div>
                  {h.reviewer_note ? (
                    <p className="mt-1 text-sm">{h.reviewer_note}</p>
                  ) : (
                    <p className="mt-1 text-sm italic text-muted-foreground">No note</p>
                  )}
                  {h.reviewed_by ? <div className="mt-0.5 text-xs text-muted-foreground">— {h.reviewed_by}</div> : null}
                </li>
              ))}
            </ul>
          ) : latestNote ? (
            <div className="border-l-2 border-border pl-3">
              <p className="text-sm">{latestNote}</p>
              {reviewedBy ? <div className="mt-0.5 text-xs text-muted-foreground">— {reviewedBy} · {fmtDate(reviewedAt)}</div> : null}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No prior notes.</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function ErrorDetail() {
  const { runId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const initialCheck = sp.get("check");

  const { data, isLoading, error } = useQuery({
    queryKey: ["run-errors", runId],
    queryFn: async () => (await api.get(`/runs/${runId}/errors`)).data,
  });
  const qc = useQueryClient();
  const refresh = () =>
    qc.invalidateQueries({ predicate: (q) => ["run-errors", "run", "client", "client-head", "finding", "finding-history", "worklist", "worklist-count"].includes(q.queryKey?.[0]) });
  const { selected, toggle: toggleSel, clear, selectAll, note, setNote, bulk } = useBulkReview({ onRefresh: refresh });

  const [selChecks, setSelChecks] = useState(() => new Set(initialCheck ? [initialCheck] : []));
  const [selSevs, setSelSevs] = useState(() => new Set());

  const toggleIn = (setFn) => (key) =>
    setFn((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const findings = data?.findings || [];
  const checks = data?.checks || [];
  const sevsPresent = useMemo(
    () => [...new Set(findings.map((f) => Number(f.severity)))].sort((a, b) => b - a),
    [findings]
  );
  const checkName = useMemo(() => Object.fromEntries(checks.map((c) => [c.check_code, c.check_name])), [checks]);

  const filtered = useMemo(
    () =>
      findings.filter(
        (f) =>
          (selChecks.size === 0 || selChecks.has(f.check_code)) &&
          (selSevs.size === 0 || selSevs.has(Number(f.severity)))
      ),
    [findings, selChecks, selSevs]
  );
  const empCount = useMemo(() => new Set(filtered.map((f) => f.payroll_employee_GUID)).size, [filtered]);
  const allSelected = filtered.length > 0 && filtered.every((f) => selected.has(f.check_result_GUID));
  const toggleAll = () => (allSelected ? clear() : selectAll(filtered.map((f) => f.check_result_GUID)));

  if (isLoading)
    return (
      <Layout>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-96 rounded-md" />
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <div className="rounded-md border border-neg/30 bg-neg/5 p-6 text-neg">
          {error.response?.status === 403 ? "You do not have access to this customer." : "Failed to load errors for this register."}
        </div>
      </Layout>
    );

  const { run } = data;
  const ccy = run.currency_code;
  const activeChips = [
    ...[...selChecks].map((c) => ({ kind: "check", value: c, label: checkName[c] || c })),
    ...[...selSevs].map((s) => ({ kind: "sev", value: s, label: (SEVERITY[s] || SEVERITY[1]).label })),
  ];

  return (
    <Layout>
      <Breadcrumbs
        items={[
          { label: "Portfolio", to: "/" },
          { label: run.client_name, to: `/clients/${run.payroll_client_GUID}` },
          { label: "Error Details" },
        ]}
      />

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-black">Error Details</h1>
          <p className="mt-1 font-mono text-sm uppercase tracking-wide text-muted-foreground">
            Register {run.run_label} · {run.run_type_code} · Pay date {fmtDate(run.pay_date)}
          </p>
        </div>
      </div>

      {/* Sticky toolbar: filters + counts + select-all stay visible on long lists */}
      <div className="sticky top-16 z-30 -mx-6 mt-4 border-y border-border bg-background/95 px-6 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="errors-select-all"
              checked={allSelected}
              onChange={toggleAll}
              className="h-4 w-4 accent-foreground cursor-pointer"
              aria-label="Select all findings"
            />
            <span data-testid="errors-count" className="inline-flex items-center gap-1.5 text-neg">
              <AlertTriangle size={15} /> <span className="font-mono font-bold">{num(filtered.length)}</span>
            </span>
            <span data-testid="employees-affected" className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Users size={15} /> <span className="font-mono font-bold text-foreground">{num(empCount)}</span>
            </span>
          </label>

          <div className="mx-1 h-6 w-px bg-border" />

          <FilterDropdown
            label="Error" testid="error-filter"
            options={checks.map((c) => ({ value: c.check_code, label: c.check_name, count: c.n, dot: sevDot(Number(c.severity)) }))}
            selected={selChecks} onToggle={toggleIn(setSelChecks)} onClear={() => setSelChecks(new Set())}
          />
          <FilterDropdown
            label="Severity" testid="severity-filter"
            options={sevsPresent.map((s) => ({ value: s, label: (SEVERITY[s] || SEVERITY[1]).label, dot: sevDot(s) }))}
            selected={selSevs} onToggle={toggleIn(setSelSevs)} onClear={() => setSelSevs(new Set())}
          />

          {activeChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="active-filters">
              {activeChips.map((chip) => (
                <button
                  key={`${chip.kind}-${chip.value}`}
                  onClick={() => (chip.kind === "check" ? toggleIn(setSelChecks)(chip.value) : toggleIn(setSelSevs)(chip.value))}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
                >
                  {chip.label} <X size={12} />
                </button>
              ))}
              <button
                data-testid="clear-all-filters"
                onClick={() => { setSelChecks(new Set()); setSelSevs(new Set()); }}
                className="text-xs font-semibold text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Findings table */}
      <section className="mt-4">
        {filtered.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground" data-testid="no-errors">
            No failed employees match the selected filters.
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border border-border bg-card min-h-[20rem] max-h-[calc(100vh-15rem)]">
            <Table className="tbl-sticky" data-testid="errors-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      data-testid="errors-select-all-head"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 accent-foreground cursor-pointer align-middle"
                      aria-label="Select all findings"
                    />
                  </TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Error &amp; Review</TableHead>
                  <TableHead className="text-right">Observed</TableHead>
                  <TableHead className="text-right">Prior</TableHead>
                  <TableHead className="text-right">Delta</TableHead>
                  <TableHead className="text-right">Threshold</TableHead>
                  <TableHead className="text-right">Gross / Net</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((f) => {
                  const ng = f.gross_pay_amount ? (f.net_pay_amount / f.gross_pay_amount) * 100 : null;
                  const isSel = selected.has(f.check_result_GUID);
                  return (
                    <TableRow
                      key={f.check_result_GUID}
                      data-testid={`error-row-${f.check_result_GUID}`}
                      className={`cursor-pointer ${isSel ? "bg-secondary" : "hover:bg-card-hover"}`}
                      onClick={() =>
                        navigate(`/runs/${runId}/employees/${f.payroll_employee_GUID}?ref=errors&check=${encodeURIComponent(f.check_code)}`)
                      }
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={`error-check-${f.check_result_GUID}`}
                          checked={isSel}
                          onChange={() => toggleSel(f.check_result_GUID)}
                          className="h-4 w-4 accent-foreground cursor-pointer align-middle"
                          aria-label="Select finding"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">{f.employee_name_as_printed}</div>
                        <div className="font-mono text-xs text-muted-foreground">ID {f.provider_employee_id || "—"}</div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{f.department_as_printed || "—"}</TableCell>
                      <TableCell className="max-w-[24rem]">
                        <div className="flex flex-wrap items-center gap-2">
                          <SeverityBadge severity={f.severity} />
                          <span className="font-medium">{f.check_name}</span>
                          <ReviewBadge status={f.review_status} />
                          {f.reviewer_note ? (
                            <NotePopover guid={f.check_result_GUID} latestNote={f.reviewer_note} reviewedBy={f.reviewed_by} reviewedAt={f.reviewed_at} />
                          ) : null}
                        </div>
                        {f.result_message ? (
                          <div className="mt-1 text-xs text-muted-foreground">{f.result_message}</div>
                        ) : null}
                        {f.reviewer_note ? (
                          <div className="mt-1 flex items-start gap-1 text-xs italic text-muted-foreground" data-testid={`note-preview-${f.check_result_GUID}`}>
                            <MessageSquare size={12} className="mt-0.5 shrink-0" />
                            <span className="truncate">{f.reviewer_note}{f.reviewed_by ? ` — ${f.reviewed_by}` : ""}</span>
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmtNum(f.observed_value)}</TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">{fmtNum(f.prior_value)}</TableCell>
                      <TableCell className="text-right">
                        <Variance delta={f.delta_value} className="justify-end" />
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">{fmtNum(f.threshold_value)}</TableCell>
                      <TableCell className="text-right">
                        <div className="text-sm"><Money value={f.gross_pay_amount} ccy={ccy} /></div>
                        <div className="text-xs text-muted-foreground">
                          <Money value={f.net_pay_amount} ccy={ccy} /> net{ng != null ? ` · ${ng.toFixed(0)}% N/G` : ""}
                        </div>
                      </TableCell>
                      <TableCell><ChevronRight size={16} className="text-muted-foreground" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {selected.size > 0 ? <div className="h-20" /> : null}

      <BulkReviewBar
        count={selected.size}
        note={note}
        setNote={setNote}
        onAction={(s) => bulk.mutate(s)}
        onClear={clear}
        pending={bulk.isPending}
      />
    </Layout>
  );
}
